/**
 * FocusSurface - Focused note view with context cards
 */
import { uiLogger } from '@annote/core/utils/debug';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { ShownIdsContext } from 'src/context/ShownContext';
import { Card } from 'src/components/Cards/Card';
import { useBrainContext } from 'src/context/BrainContext';
import { useNotes } from 'src/context/noteCacheHooks';
import { isAnnote, Note } from '@annote/types';
import { usePanelContext } from 'src/context/PanelContext';
import { dimensions } from '@annote/core/styles';

const MAX_DEPTH = 4;
const MIN_DEPTH = 0;

const Surface = styled.div<{ $leftOffsetRem?: number; $rightOffsetRem?: number }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  height: 100vh;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  /* account for left/right panels overlaying the surface */
  padding-left: ${({ $leftOffsetRem }) => ($leftOffsetRem ? `${$leftOffsetRem}rem` : '0')};
  padding-right: ${({ $rightOffsetRem }) => ($rightOffsetRem ? `${$rightOffsetRem}rem` : '0')};
  overflow-y: hidden;
  background-size: 10px 10px;
  background-position: 0 0;
`;

const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  width: 40rem;
  margin: 0 2rem;
  max-height: 100vh; /* Limit height to viewport */
  overflow-y: auto; /* Enable vertical scrolling */
  padding: 1rem; /* Add some padding for better appearance */
  position: relative; /* Added for sticky header positioning context */
`;

const ColumnGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  margin: 0 2rem;
  margin-top: 8rem;
`;

const ColumnContainer = styled(ColumnGroup)`
  flex-direction: row;
`;

const PinnedColumn = styled(ColumnGroup)`
  width: fit-content;
  align-items: center;
`;

const CardWrapper = styled.div`
  padding-bottom: 2rem;
`;

const GroupHeader = styled.h2`
  margin-bottom: 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Button = styled.button`
  margin-left: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  background-color: #f0f0f0;
  cursor: pointer;
`;

const LinkDepthSelector = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const BacklinkDepthSelector = styled(LinkDepthSelector)`
  margin-left: 1rem;
`;

const FocusSurface: React.FC = () => {
  const { shownIds, clear, show, updateSize } = useContext(ShownIdsContext);
  const { leftPanelsWidth, rightPanelsWidth } = usePanelContext();

  // Convert panel widths from pixels to rems to match other components
  const leftOffsetRem = leftPanelsWidth / dimensions.rem2px;
  const rightOffsetRem = rightPanelsWidth / dimensions.rem2px;
  const { getNotes } = useBrainContext();
  const [linkDepth, setLinkDepth] = useState(1);
  const [backlinkDepth, setBacklinkDepth] = useState(1);
  const [resolvedLinksByDepth, setResolvedLinksByDepth] = useState<Note[][]>([]);
  const [resolvedBacklinksByDepth, setResolvedBacklinksByDepth] = useState<Note[][]>([]);

  // Subscribe to the center note reactively
  const [centerNote] = useNotes(shownIds[0] ? [shownIds[0]] : []);

  // const links = useMemo(
  //   () => focusedNote?.links.map((linkId) => getCachedNote(linkId)).filter((n) => n),
  //   [focusedNote?.links, getCachedNote],
  // );

  // const backlinks = useMemo(
  //   () => focusedNote?.backlinks.map((linkId) => getCachedNote(linkId)).filter((n) => n),
  //   [focusedNote?.backlinks, getCachedNote],
  // );

  const getNotesByDepth = useCallback(
    async (linkOrBacklink: 'link' | 'backlink', depth: number): Promise<Note[][]> => {
      if (!centerNote) return [];
      const notesByDepth: Note[][] = [];
      for (let i = 0; i < depth; i += 1) {
        const previousNotes = i === 0 ? [centerNote] : notesByDepth[i - 1] || [];
        const previousIds = notesByDepth.flatMap((notes) => notes.map((note) => note.id));

        const connectionIds =
          previousNotes
            .filter((note) => note)
            .flatMap((note) => (linkOrBacklink === 'link' ? note.links : note.backlinks)) || [];
        // TODO - we don't have graph style querying yet so this will have to do for now
        // eslint-disable-next-line no-await-in-loop
        const newConnectionIds = connectionIds.filter((id) => !previousIds.includes(id));
        if (newConnectionIds.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          const connections = await getNotes(newConnectionIds);
          notesByDepth.push(connections);
        }
      }
      return notesByDepth;
    },
    [centerNote, getNotes],
  );

  useEffect(() => {
    const fetchLinks = async (): Promise<void> => {
      const links = await getNotesByDepth('link', linkDepth);
      setResolvedLinksByDepth(links.reverse() || []);
    };
    fetchLinks();
  }, [getNotesByDepth, linkDepth]);

  useEffect(() => {
    const fetchBacklinks = async (): Promise<void> => {
      const backlinks = await getNotesByDepth('backlink', backlinkDepth);
      setResolvedBacklinksByDepth(backlinks || []);
    };
    fetchBacklinks();
  }, [getNotesByDepth, backlinkDepth]);

  const pinCard = (cardId: string): void => {
    clear();
    show([cardId]);
  };

  useEffect(() => {
    if (!centerNote) return;
    uiLogger.debug('centering on note', centerNote);
    clear();
    show([centerNote.id]);
    updateSize(centerNote.id, 'LARGE');
  }, [centerNote]);

  return (
    <Surface id="surface" $leftOffsetRem={leftOffsetRem} $rightOffsetRem={rightOffsetRem}>
      <ColumnGroup>
        <GroupHeader>
          Links
          <LinkDepthSelector>
            <Button onClick={() => setLinkDepth(Math.min(linkDepth + 1, MAX_DEPTH))}>+</Button>
            <Button onClick={() => setLinkDepth(Math.max(linkDepth - 1, MIN_DEPTH))}>-</Button>
          </LinkDepthSelector>
        </GroupHeader>
        <ColumnContainer>
          {resolvedLinksByDepth.map((links, index) => (
            <CardColumn key={`link-column-${index}`} id={`link-column-${index}`}>
              <h3>Depth {resolvedLinksByDepth.length - index}</h3>
              {links.map(
                (link) =>
                  !isAnnote(link) && ( // don't show annotes optionally.
                    <CardWrapper key={`link-wrapper-${link.id}`} onDoubleClick={() => pinCard(link.id)}>
                      <Card key={`link-${link.id}`} note={link} />
                    </CardWrapper>
                  ),
              )}
            </CardColumn>
          ))}
        </ColumnContainer>
      </ColumnGroup>
      <PinnedColumn>
        <GroupHeader>Focused Card</GroupHeader>
        {centerNote && <Card key={centerNote.id} note={centerNote} />}
      </PinnedColumn>
      <ColumnGroup>
        <GroupHeader>
          Backlinks
          <BacklinkDepthSelector>
            <Button onClick={() => setBacklinkDepth(Math.min(backlinkDepth + 1, MAX_DEPTH))}>+</Button>
            <Button onClick={() => setBacklinkDepth(Math.max(backlinkDepth - 1, MIN_DEPTH))}>-</Button>
          </BacklinkDepthSelector>
        </GroupHeader>
        <ColumnContainer>
          {resolvedBacklinksByDepth.map((backlinks, index) => (
            <CardColumn key={`backlink-column-${index}`}>
              {backlinks.map((backlink) => (
                <CardWrapper key={`backlink-wrapper-${backlink.id}`} onDoubleClick={() => pinCard(backlink.id)}>
                  <Card key={`backlink-${backlink.id}`} note={backlink} />
                </CardWrapper>
              ))}
            </CardColumn>
          ))}
        </ColumnContainer>
      </ColumnGroup>
    </Surface>
  );
};

export default FocusSurface;
